import { NavLink, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../context/AuthContext'
import { getSettings } from '../api/settings'
import { useT } from '../i18n'

const G   = '#C89B4C'
const TX  = '#F4F1EA'
const TX3 = '#8A8480'
const BD  = 'rgba(244,238,225,0.12)'
const GLASS = {
  background: 'rgba(13,21,38,0.90)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
} as const
const MONO = "'JetBrains Mono', monospace"

const Icons = {
  home:     <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9.5L12 3l9 6.5V20a1 1 0 01-1 1H4a1 1 0 01-1-1V9.5z"/><polyline points="9 21 9 12 15 12 15 21"/></svg>,
  orders:   <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 3H6a2 2 0 00-2 2v14a2 2 0 002 2h12a2 2 0 002-2V9z"/><polyline points="14 3 14 9 20 9"/></svg>,
  catalog:  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>,
  profile:  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 21v-1a6 6 0 016-6h4a6 6 0 016 6v1"/></svg>,
  plus:     <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  dash:     <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="9" rx="1"/><rect x="14" y="3" width="7" height="5" rx="1"/><rect x="14" y="12" width="7" height="9" rx="1"/><rect x="3" y="16" width="7" height="5" rx="1"/></svg>,
  payments: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="12" cy="6" rx="8" ry="3"/><path d="M4 6v6c0 1.7 3.6 3 8 3s8-1.3 8-3V6"/><path d="M4 12v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6"/></svg>,
  products: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>,
  more:     <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>,
}

export default function DesktopShell() {
  const { user, isAdmin } = useAuth()
  const lang = user?.language ?? 'ru'
  const tr = useT(lang)
  const navigate = useNavigate()

  const { data: settings } = useQuery({ queryKey: ['settings'], queryFn: getSettings, staleTime: 60_000 })
  const usdRate = settings?.usd_rate ?? 12000

  const adminNav = [
    { to: '/admin',           end: true,  icon: Icons.dash,     label: tr('overview') },
    { to: '/admin/orders',                icon: Icons.orders,   label: tr('orders') },
    { to: '/admin/payments',              icon: Icons.payments, label: tr('payments') },
    { to: '/admin/products',              icon: Icons.products, label: tr('products') },
    { to: '/admin/more',                  icon: Icons.more,     label: tr('more') },
  ]

  const clientNav = [
    { to: '/home',    end: true, icon: Icons.home,    label: tr('home') },
    { to: '/orders',             icon: Icons.orders,  label: tr('orders') },
    { to: '/catalog',            icon: Icons.catalog, label: tr('catalog') },
    { to: '/profile',            icon: Icons.profile, label: tr('profile') },
  ]

  const navItems = isAdmin ? adminNav : clientNav

  const linkStyle = (isActive: boolean): React.CSSProperties => ({
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '10px 16px', borderRadius: 10, margin: '1px 0',
    color: isActive ? G : TX3,
    textDecoration: 'none',
    background: isActive ? 'rgba(200,155,76,0.10)' : 'transparent',
    border: isActive ? '1px solid rgba(200,155,76,0.18)' : '1px solid transparent',
    fontSize: 13, fontWeight: isActive ? 600 : 400,
    fontFamily: "'Inter', sans-serif",
    transition: 'all 0.15s',
  })

  return (
    // desktop-only — hidden on mobile via CSS
    <div className="desktop-only" style={{ position: 'fixed', inset: 0, zIndex: 200, pointerEvents: 'none', flexDirection: 'column' }}>

      {/* ── Top bar ──────────────────────────────────────────── */}
      <header style={{
        pointerEvents: 'auto',
        position: 'absolute', top: 0, left: 0, right: 0, height: 60,
        ...GLASS,
        borderBottom: `1px solid ${BD}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 24px 0 0',
      }}>
        {/* Brand — sits inside the sidebar width */}
        <div style={{
          width: 200, flexShrink: 0,
          display: 'flex', alignItems: 'center',
          padding: '0 20px',
          borderRight: `1px solid ${BD}`,
          height: '100%',
        }}>
          <div>
            <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: TX, letterSpacing: '0.05em', lineHeight: 1.1 }}>ANSOR</p>
            <p style={{ margin: 0, fontSize: 8, fontWeight: 600, color: G, letterSpacing: '0.18em', textTransform: 'uppercase' }}>NASIYA SAVDO</p>
          </div>
        </div>

        {/* Exchange rate */}
        <p style={{ margin: 0, fontSize: 12, color: TX3, fontFamily: MONO }}>
          $1 = {usdRate.toLocaleString('ru-RU')} {lang === 'uz' ? "so'm" : 'сум'}
        </p>
      </header>

      {/* ── Sidebar ──────────────────────────────────────────── */}
      <aside style={{
        pointerEvents: 'auto',
        position: 'absolute', top: 60, left: 0, bottom: 0, width: 200,
        ...GLASS,
        borderRight: `1px solid ${BD}`,
        display: 'flex', flexDirection: 'column',
        padding: '12px 12px 24px',
      }}>
        <nav style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {navItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={(item as any).end}
              style={({ isActive }) => linkStyle(isActive)}
            >
              {item.icon}
              <span>{item.label}</span>
            </NavLink>
          ))}

          {/* Client: custom order button */}
          {!isAdmin && (
            <button
              onClick={() => navigate('/custom-order')}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '10px 16px', borderRadius: 10, margin: '1px 0',
                background: 'rgba(200,155,76,0.10)',
                border: '1px solid rgba(200,155,76,0.25)',
                color: G, fontSize: 13, fontWeight: 500,
                fontFamily: "'Inter', sans-serif",
                cursor: 'pointer', width: '100%', textAlign: 'left',
                marginTop: 8,
              }}
            >
              {Icons.plus}
              <span>{lang === 'uz' ? "Ariza berish" : 'Подать заявку'}</span>
            </button>
          )}
        </nav>
      </aside>
    </div>
  )
}
